import * as Sentry from "@sentry/react";
import type { Locale } from "date-fns";
import { enUS, es, fr, it, nl, pt } from "date-fns/locale";
import { I18n } from "i18n-js";
import localeEn from "@/locales/en.json" with { type: "json" };
import localeEs from "@/locales/es.json" with { type: "json" };
import localeFr from "@/locales/fr.json" with { type: "json" };
import localeIt from "@/locales/it.json" with { type: "json" };
import localeNl from "@/locales/nl.json" with { type: "json" };
import localePt from "@/locales/pt.json" with { type: "json" };

interface ToSmartCurrencyConfig {
  noDecimal?: boolean;
  currency?: string;
  pricePerHour?: boolean;
}

interface ToDistanceConfig {
  small?: boolean;
  noFormat?: boolean;
}

interface CustomI18n extends Omit<I18n, "toTime"> {
  currency: string;
  currencyLong: string;
  country: string;
  dateFnsLocale: Locale;
  toDistance: (distance: number, config?: ToDistanceConfig) => string;
  toVolume: (volume: number) => string;
  toSmartCurrency: (amount: number | string, config?: ToSmartCurrencyConfig) => string;
  toDuration: (hours: number, isDuration?: boolean, noDecimal?: boolean) => string;
  toTime: (time: number) => string;
  toKm: (distance: number) => number;
}

const country = document.documentElement.dataset?.country || "fr";

const IMPERIAL_COUNTRIES = ["UK"];

const LOCALE_CONFIG = {
  fr: { locale: localeFr, dateFnsLocale: fr },
  fr_CH: { locale: localeFr, dateFnsLocale: fr },
  fr_BE: { locale: localeFr, dateFnsLocale: fr },
  fr_LU: { locale: localeFr, dateFnsLocale: fr },
  nl: { locale: localeNl, dateFnsLocale: nl },
  en: { locale: localeEn, dateFnsLocale: enUS },
  en_IE: { locale: localeEn, dateFnsLocale: enUS },
  es: { locale: localeEs, dateFnsLocale: es },
  pt: { locale: localePt, dateFnsLocale: pt },
  it: { locale: localeIt, dateFnsLocale: it },
};

const i18n = new I18n({
  fr: LOCALE_CONFIG.fr.locale,
  fr_CH: LOCALE_CONFIG.fr_CH.locale,
  fr_BE: LOCALE_CONFIG.fr_BE.locale,
  fr_LU: LOCALE_CONFIG.fr_LU.locale,
  nl: LOCALE_CONFIG.nl.locale,
  en: LOCALE_CONFIG.en.locale,
  en_IE: LOCALE_CONFIG.en_IE.locale,
  es: LOCALE_CONFIG.es.locale,
  pt: LOCALE_CONFIG.pt.locale,
  it: LOCALE_CONFIG.it.locale,
});

i18n.locale = document.documentElement.lang.replace("-", "_");

const currency = () => {
  switch (country) {
    case "ch":
      return "CHF";
    case "uk":
      return "£";
    default:
      return "€";
  }
};

const currencyLong = () => {
  switch (country) {
    case "ch":
      return "CHF";
    case "uk":
      return "GBP";
    default:
      return "EUR";
  }
};

const getDateFnsLocale = () => {
  return LOCALE_CONFIG[i18n.locale].dateFnsLocale;
};

const toKm: CustomI18n["toKm"] = (distance) => {
  const isImperial = IMPERIAL_COUNTRIES.includes(country.toUpperCase());
  if (isImperial) {
    return Math.round(distance / 1.609_34);
  }
  return distance;
};

const toDuration: CustomI18n["toDuration"] = (hours, isDuration, noDecimal) => {
  const newHours = isDuration ? hours : hours - (hours >= 24 ? 24 : 0);
  if (newHours % 1 === 0) {
    if (i18n.locale === "nl") {
      const decimal = noDecimal ? "urr" : " uur";
      return `${Math.floor(newHours)}${decimal}`;
    }
    const decimal = noDecimal ? "h" : "h00";
    return `${newHours}${decimal}`;
  }
  if (i18n.locale === "nl") {
    return `${Math.floor(newHours)} uur 30`;
  }
  return `${Math.floor(newHours)}h30`;
};

const toTime: CustomI18n["toTime"] = (time) => {
  let res: string;
  if (time % 1 === 0) {
    res = `${time}:00`;
  } else {
    res = `${Math.floor(time)}:30`;
  }
  return res.padStart(5, "0");
};

const toSmartCurrency: CustomI18n["toSmartCurrency"] = (amount, config = {}) => {
  const parsedAmount = Number(amount);
  const hasDecimal = parsedAmount % 1 !== 0;

  let fixedAmount: string;
  if (hasDecimal || !config?.noDecimal) {
    fixedAmount = parsedAmount.toFixed(2);
  } else {
    fixedAmount = parsedAmount.toFixed(0);
  }

  const price = `${fixedAmount.replace(".", ",")}${String.fromCharCode(160)}${
    config?.currency || currency()
  }`;
  return config.pricePerHour ? i18n.t("common.price_per_hour", { price }) : price;
};

const onMissingKey = (key) => {
  Sentry.captureException(`Missing translation for key: ${key}`);
  console.error("Missing translation for key:", key);
  return key;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Can't be simplified
const toDistance: CustomI18n["toDistance"] = (distance, config) => {
  let newDistance = distance;
  let unit: string;
  const isImperial = IMPERIAL_COUNTRIES.includes(country.toUpperCase());
  if (config?.small) {
    newDistance = isImperial ? newDistance * 0.3937 : newDistance;
    unit = isImperial
      ? i18n.t("common.n_inch", {
          n: newDistance.toFixed(2).toString().replace(".", ","),
          count: newDistance,
        })
      : i18n.t("common.n_centimeters_short", {
          n: newDistance.toFixed(2).toString().replace(".", ","),
        });
    return unit;
  }
  if (config?.noFormat) {
    unit = isImperial
      ? i18n.t("common.miles", { count: newDistance })
      : i18n.t("common.kilometers_short", { count: newDistance });
    return `${isImperial ? newDistance : newDistance.toString().replace(".", ",")} ${unit}`;
  }
  if (newDistance >= 1000) {
    newDistance = isImperial ? newDistance / 5280 : newDistance / 1000;
    unit = isImperial
      ? i18n.t("common.miles", { count: newDistance })
      : i18n.t("common.kilometers_short", { count: newDistance });
  } else {
    unit = isImperial ? i18n.t("common.feet") : i18n.t("common.meters", { count: newDistance });
  }

  return `${newDistance.toFixed(isImperial && newDistance >= 1000 ? 2 : 0)} ${unit}`;
};

const toVolume: CustomI18n["toVolume"] = (volume) => {
  const isImperial = IMPERIAL_COUNTRIES.includes(country.toUpperCase());
  const convertedVolume = isImperial ? volume * 35.3147 : volume;
  const unit = isImperial ? "ft³" : "m³";
  return `${convertedVolume.toFixed(2)} ${unit}`;
};

i18n.defaultLocale = "fr";
i18n.enableFallback = true;
i18n.missingTranslation.get = onMissingKey;

const polyglot = i18n as unknown as CustomI18n;
polyglot.currency = currency();
polyglot.toDistance = toDistance;
polyglot.toVolume = toVolume;
polyglot.currencyLong = currencyLong();
polyglot.country = country;
polyglot.toSmartCurrency = toSmartCurrency;
polyglot.toDuration = toDuration;
polyglot.toTime = toTime;
polyglot.toKm = toKm;
polyglot.dateFnsLocale = getDateFnsLocale();

export default polyglot;
